// Classify propose-apply failures and ignore stale in-flight requests.
// LaunchError drops the axios timeout code (ECONNABORTED), but the 90 s cut
// here is a defect, so timeouts are reported with a synthetic error. Network
// and HTTP failures go to LaunchError with status, code, and message only, so
// the shared filters (ERR_NETWORK, integration not found) still apply and the
// bearer token, email, and brand payload never reach Sentry or console.
// HTTP 200 {ok:false} rejections are not reported here; the backend already
// reports them. User cancels are not reported.

export const PROPOSE_APPLY_TIMEOUT_MS = 90 * 1000;

// A warm-up only needs the instance to boot, so it can be cut earlier than the
// real call. It never blocks the UI, it just stops a hung request from lingering.
export const PROPOSE_WARM_UP_TIMEOUT_MS = 30 * 1000;

const PROPOSE_WARM_UP_STORAGE_KEY = "adle.brandInsightsApplyWarmedUp";

// Secondary guard for private mode / blocked storage: at most once per session
// in memory. sessionStorage is the primary one so a redeploy plus reload can
// warm the new revision again.
let warmUpDoneInMemory = false;

export const isProposeWarmUpDone = () => {
  if (warmUpDoneInMemory) return true;
  try {
    return window.sessionStorage.getItem(PROPOSE_WARM_UP_STORAGE_KEY) === "1";
  } catch (error) {
    return warmUpDoneInMemory;
  }
};

export const markProposeWarmUpDone = () => {
  warmUpDoneInMemory = true;
  try {
    window.sessionStorage.setItem(PROPOSE_WARM_UP_STORAGE_KEY, "1");
  } catch (error) {
    // Storage blocked: the in-memory flag already prevents repeats.
  }
};

// A failed warm-up must not block the next visit: the instance may never have
// booted, so allow another attempt.
export const resetProposeWarmUpDone = () => {
  warmUpDoneInMemory = false;
  try {
    window.sessionStorage.removeItem(PROPOSE_WARM_UP_STORAGE_KEY);
  } catch (error) {
    // Storage blocked: nothing to clear beyond the in-memory flag.
  }
};

const AI_REQUEST_TIMEOUT_CODE = "aiRequestTimeout";

// Same domain results the backend treats as expected apply rejections.
// validation-failed stays out of this set on purpose.
const EXPECTED_BRAND_INSIGHT_APPLY_REJECTIONS = new Set([
  "already-in-config",
  "unmappable",
  "off-allowlist",
  "empty-edits",
  "rewrite",
  "missing-insight",
  "language-undetermined",
]);

const APPLY_TIMEOUT_REASONS = new Set(["timeout", "deadline-exceeded"]);

export const isExpectedBrandInsightApplyRejection = (reason) =>
  EXPECTED_BRAND_INSIGHT_APPLY_REJECTIONS.has(reason);

// axios.isCancel reads __CANCEL__. Axios 1 also sets ERR_CANCELED.
const isCanceledRequest = (error) =>
  error?.__CANCEL__ === true ||
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

const classifyProposeBrandInsightApplyError = (error) => {
  if (isCanceledRequest(error)) return "canceled";
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
    return "timeout";
  }
  if (error?.response) return `http-${error.response.status}`;
  if (error?.code === "ERR_NETWORK") return "network";
  return "unexpected";
};

const resolveApplyFailureReason = ({ reason, aiCode } = {}) => {
  const isTimeout =
    APPLY_TIMEOUT_REASONS.has(reason) || aiCode === AI_REQUEST_TIMEOUT_CODE;
  if (isTimeout) return "timeout";
  return reason || "rejected";
};

const SECRET_MESSAGE = /Bearer\s+\S|Authorization|@/;

// LaunchError console.logs the error it receives, and an axios error carries
// the bearer token plus the request body. Keep only the fields the shared
// Sentry filters and the timeout diagnosis need.
const sentrySafeRequestError = (error) => {
  if (!error || typeof error !== "object") return error;
  const hasTransportPayload =
    "config" in error || "request" in error || "response" in error;
  if (!hasTransportPayload) return error;

  const data = error.response?.data;
  const responseCode =
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    typeof data.code === "string"
      ? data.code
      : undefined;
  const status = error.response?.status;
  const safe = {};
  if (typeof error.name === "string") safe.name = error.name;
  if (error.code !== undefined && error.code !== null) safe.code = error.code;
  if (
    typeof error.message === "string" &&
    !SECRET_MESSAGE.test(error.message)
  ) {
    safe.message = error.message;
  }
  if (status !== undefined || responseCode !== undefined) {
    safe.response = {};
    if (status !== undefined) safe.response.status = status;
    if (responseCode !== undefined) safe.response.data = { code: responseCode };
  }
  return safe;
};

// The axios error is not attached as `cause`: its config carries the bearer
// token and LaunchError logs the error it receives.
const buildSyntheticError = ({ reason }) => {
  const reportError = new Error(`proposeBrandInsightApply rejected: ${reason}`);
  reportError.code = reason;
  return reportError;
};

const buildLaunchErrorCall = ({ error, reason, cause }) => {
  return {
    error,
    showAlert: false,
    ignoreAlertByErrors: [],
    sentryMeta: {
      functionName: "startProposeBrandInsightApply",
      context: {
        reason,
        ...(cause?.code ? { causeCode: String(cause.code) } : {}),
        ...(cause?.message ? { causeMessage: cause.message } : {}),
      },
      tags: {
        action: "proposeBrandInsightApply",
        reason,
      },
    },
  };
};

const resolveProposeBrandInsightApply = ({ data, error } = {}) => {
  if (error) {
    const reason = classifyProposeBrandInsightApplyError(error);
    if (reason === "canceled") {
      return { result: { ok: false, reason }, launchError: null };
    }
    const safeCause = sentrySafeRequestError(error);
    return {
      result: { ok: false, reason },
      launchError: buildLaunchErrorCall({
        error:
          reason === "timeout" ? buildSyntheticError({ reason }) : safeCause,
        reason,
        cause: safeCause,
      }),
    };
  }

  // HTTP 200 {ok:false} is a domain result. The backend reports it, including
  // unexpected reasons such as ai-plan-failed. Expected reasons stay listed
  // above (language-undetermined included, validation-failed excluded).
  return { result: data, launchError: null };
};

export const settleProposeBrandInsightApply = ({
  data,
  error,
  dispatch,
  launchError,
}) => {
  const outcome = resolveProposeBrandInsightApply({ data, error });
  if (outcome.launchError) {
    const {
      error: reportError,
      showAlert,
      ignoreAlertByErrors,
      sentryMeta,
    } = outcome.launchError;
    dispatch(
      launchError(reportError, showAlert, ignoreAlertByErrors, sentryMeta)
    );
  }
  return outcome.result;
};

export const createApplyProposeSession = () => {
  let requestId = 0;
  let controller = null;

  const invalidate = () => {
    const active = controller;
    controller = null;
    requestId += 1;
    active?.abort();
  };

  return {
    start: () => {
      controller?.abort();
      requestId += 1;
      const id = requestId;
      controller = new AbortController();
      return { requestId: id, signal: controller.signal };
    },
    isCurrent: (id) => requestId === id,
    close: () => {
      invalidate();
      return { isProposing: false };
    },
    finish: (id) => {
      if (requestId !== id) return false;
      controller = null;
      return true;
    },
    abort: () => {
      invalidate();
    },
  };
};

export const reduceApplyProposeResult = ({ isCurrent, result }) => {
  if (!isCurrent || result?.reason === "canceled") {
    return { ignored: true };
  }
  if (!result?.ok) {
    return {
      ignored: false,
      applyError: resolveApplyFailureReason({
        reason: result?.reason,
        aiCode: result?.aiCode,
      }),
      proposal: null,
    };
  }
  return {
    ignored: false,
    applyError: "",
    proposal: result?.data ?? null,
  };
};
